import React, { useEffect } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { track as trackEvent } from '../lib/analytics'

/**
 * Thin onboarding entrypoint.
 *
 * 2026-05-19: the custom onboarding UI was removed so the app reuses the
 * real `/universities` and `/courses/:shortName` pages instead of maintaining
 * a second copy that kept drifting in copy, layout, and behaviour.
 *
 * This route remains the canonical cold-entry target for anonymous-first
 * visitors and legacy redirects, but it now acts only as:
 *   1. anonymous funnel entry analytics
 *   2. a small state-based redirect shell
 */

const ANON_ONBOARDING_START_KEY = 'anon_funnel.onboarding_started'

function maybeFireOnboardingStart(studentId: string): void {
  try {
    const raw = localStorage.getItem(ANON_ONBOARDING_START_KEY)
    const seen = raw ? new Set<string>(JSON.parse(raw)) : new Set<string>()
    if (seen.has(studentId)) return
    seen.add(studentId)
    localStorage.setItem(ANON_ONBOARDING_START_KEY, JSON.stringify([...seen]))
    trackEvent('anon_funnel.onboarding_start', { student_id: studentId })
  } catch {
    trackEvent('anon_funnel.onboarding_start', { student_id: studentId })
  }
}

export const OnboardingPage: React.FC = () => {
  const { user, loading } = useAuth()

  useEffect(() => {
    if (user?.is_anonymous && user.id) maybeFireOnboardingStart(user.id)
  }, [user?.id, user?.is_anonymous])

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-400 text-sm">A carregar…</div>
      </div>
    )
  }

  if (user.role === 'teacher') return <Navigate to="/teacher" replace />
  if (user.role === 'admin' || user.is_admin) return <Navigate to="/admin" replace />
  if (user.has_programme) return <Navigate to="/subjects" replace />

  return <Navigate to="/universities" replace />
}

export default OnboardingPage
